import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../api';

const SessionComparison = ({ baseSessionId, compareSessionId: compareFromProps = '', onUpdateCompareId, onSwap, onBack, allSessions }) => {
  const [compareSessionId, setCompareSessionId] = useState(compareFromProps);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // keep local state in sync with URL/props
  useEffect(() => { setCompareSessionId(compareFromProps || ''); }, [compareFromProps]);

  const loadComparison = async () => {
    if (!compareSessionId) {
      alert('Please select a session to compare');
      return;
    }

    if (compareSessionId === baseSessionId) {
      alert('Please select a different session to compare');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/api/sessions/compare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_1: baseSessionId,
          session_2: compareSessionId
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to compare sessions: ${response.status}`);
      }

      const data = await response.json();
      setComparison(data);
    } catch (err) {
      console.error('Error comparing sessions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (sessionId) => {
    const match = sessionId.match(/audio(\d{4})/);
    if (match) {
      const dateCode = match[1];
      const month = dateCode.substring(0, 2);
      const day = dateCode.substring(2, 4);
      return `${month}/${day}`;
    }
    return sessionId;
  };

  // Available sessions for comparison (exclude the base session)
  const availableSessions = allSessions ?
    allSessions.filter(s => s.id !== baseSessionId) : [];

  const onSelectCompare = (val) => {
    setCompareSessionId(val);
    if (onUpdateCompareId) onUpdateCompareId(val);
  };

  const copyLink = () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('id', baseSessionId);
      if (compareSessionId) url.searchParams.set('compare', compareSessionId);
      navigator.clipboard.writeText(url.toString());
      window.dispatchEvent(new CustomEvent('app-toast', { detail: 'Link copied' }));
    } catch {}
  };

  const metrics = useMemo(() => {
    if (!comparison) return null;
    const t1 = comparison.session_1?.topics?.length || 0;
    const t2 = comparison.session_2?.topics?.length || 0;
    const o1 = comparison.session_1?.learning_outcomes?.length || 0;
    const o2 = comparison.session_2?.learning_outcomes?.length || 0;
    return { t1, t2, o1, o2, dt: t2 - t1, do: o2 - o1 };
  }, [comparison]);

  return (
    <div style={{ padding: '0.5rem 0.25rem 1rem' }}>
      <div className="compare-controls">
        <div className="crumb">Session {formatDate(baseSessionId)} → Compare</div>
        <div className="spacer" />
        <div className="picker">
          <label>With</label>
          <select value={compareSessionId} onChange={(e) => onSelectCompare(e.target.value)}>
            <option value="">Select…</option>
            {availableSessions.map((session) => (
              <option key={session.id} value={session.id}>
                {formatDate(session.id)}
              </option>
            ))}
          </select>
          <button className="accent" onClick={loadComparison} disabled={!compareSessionId || loading}>
            {loading ? 'Analyzing…' : 'Compare'}
          </button>
          <button className="ghost" onClick={onSwap} disabled={!compareSessionId} title="Swap baseline">⇄ Swap</button>
          <button className="ghost" onClick={copyLink} title="Copy link">🔗</button>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <div className="lrbrain-typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p style={{ marginTop: '1rem', color: 'var(--lrbrain-text)', opacity: 0.7 }}>
            Analyzing progress between sessions...
          </p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div style={{
          padding: '1.5rem',
          background: 'rgba(239, 68, 68, 0.1)',
          borderRadius: '12px',
          color: 'var(--lrbrain-error)',
          textAlign: 'center'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Comparison results */}
      {comparison && !loading && (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {/* Summary strip */}
          <div className="summary-strip">
            <div className="delta">Topics Δ: <span className={metrics?.dt > 0 ? 'pos' : metrics?.dt < 0 ? 'neg' : ''}>{metrics?.dt}</span></div>
            <div className="delta">Outcomes Δ: <span className={metrics?.do > 0 ? 'pos' : metrics?.do < 0 ? 'neg' : ''}>{metrics?.do}</span></div>
            <div className="delta muted">Base {formatDate(comparison.session_1.id)} vs {formatDate(comparison.session_2.id)}</div>
          </div>

          {/* Progress Highlights */}
          <div style={{
            background: 'linear-gradient(135deg, var(--lrbrain-success), #10b981)',
            borderRadius: '16px',
            padding: '1.5rem',
            color: 'white'
          }}>
            <h3 style={{
              fontFamily: 'var(--lrbrain-font-fun)',
              margin: '0 0 1rem 0',
              fontSize: '1.5rem'
            }}>
              ✨ Progress Highlights
            </h3>

            {comparison.analysis.challenges_resolved.length > 0 ? (
              <div style={{ marginBottom: '1rem' }}>
                <p style={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  margin: '0 0 0.5rem 0',
                  opacity: 0.95
                }}>
                  🎯 Challenges Resolved: {comparison.analysis.challenges_resolved.length}
                </p>
                <ul style={{
                  margin: 0,
                  paddingLeft: '1.5rem',
                  opacity: 0.9
                }}>
                  {comparison.analysis.challenges_resolved.map((challenge, idx) => (
                    <li key={idx} style={{ lineHeight: '1.8' }}>
                      {challenge}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p style={{ margin: 0, opacity: 0.9 }}>
                No previously identified challenges were fully resolved, but learning continues!
              </p>
            )}
          </div>

          {/* New Topics Explored */}
          {comparison.analysis.new_topics_in_session_2.length > 0 && (
            <ComparisonCard
              title="🆕 New Topics Explored"
              color="var(--lrbrain-accent)"
              items={comparison.analysis.new_topics_in_session_2}
            />
          )}

          {/* Continued Topics */}
          {comparison.analysis.topics_overlap.length > 0 && (
            <ComparisonCard
              title="🔄 Continued Topics (Reviewed/Reinforced)"
              color="var(--lrbrain-purple)"
              items={comparison.analysis.topics_overlap}
            />
          )}

          {/* Persistent Challenges */}
          {comparison.analysis.persistent_challenges.length > 0 && (
            <ComparisonCard
              title="⚠️ Ongoing Challenges (Needs More Practice)"
              color="var(--lrbrain-warning)"
              items={comparison.analysis.persistent_challenges}
            />
          )}

          {/* Diff legend and side-by-side session details */}
          <div className="diff-legend">
            <span className="item pos">+ Added</span>
            <span className="item neg">− Removed</span>
            <span className="item neu">• Unchanged</span>
          </div>

          <div className="compare-grid">
            <SessionDiffCard
              title={`Session ${formatDate(comparison.session_1.id)}`}
              session={comparison.session_1}
              otherSession={comparison.session_2}
              color="var(--lrbrain-navy)"
              perspective="base"
            />
            <SessionDiffCard
              title={`Session ${formatDate(comparison.session_2.id)}`}
              session={comparison.session_2}
              otherSession={comparison.session_1}
              color="var(--lrbrain-accent)"
              perspective="compare"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Comparison card component
const ComparisonCard = ({ title, color, items }) => (
  <div style={{
    background: 'white',
    borderRadius: '12px',
    border: `2px solid ${color}`,
    overflow: 'hidden'
  }}>
    <div style={{
      background: color,
      color: 'white',
      padding: '0.75rem 1rem',
      fontFamily: 'var(--lrbrain-font-fun)',
      fontSize: '1rem',
      fontWeight: 600
    }}>
      {title}
    </div>
    <div style={{ padding: '1rem' }}>
      {items.length > 0 ? (
        <ul style={{
          margin: 0,
          paddingLeft: '1.5rem',
          listStyleType: 'disc'
        }}>
          {items.map((item, idx) => (
            <li key={idx} style={{
              fontSize: '0.9375rem',
              lineHeight: '1.8',
              color: 'var(--lrbrain-text)'
            }}>
              {item}
            </li>
          ))}
        </ul>
      ) : (
        <p style={{ margin: 0, color: 'var(--lrbrain-text)', opacity: 0.7 }}>
          None identified
        </p>
      )}
    </div>
  </div>
);

// Diff helpers and diff-aware cards
const normSet = (arr = []) => new Set(arr.map(x => (x || '').toString().trim().toLowerCase()).filter(Boolean));

const DiffList = ({ items = [], otherItems = [], perspective = 'base' }) => {
  const other = normSet(otherItems);
  return (
    <ul className="diff-list">
      {items.map((item, idx) => {
        const key = (item || '').toString();
        const normalized = key.trim().toLowerCase();
        const isShared = other.has(normalized);
        const isPos = !isShared && perspective === 'compare';
        const isNeg = !isShared && perspective === 'base';
        const cls = isPos ? 'pos' : isNeg ? 'neg' : 'neu';
        const mark = isPos ? '+' : isNeg ? '−' : '•';
        return (
          <li key={idx} className={cls}>
            <span className="mark">{mark}</span>
            <span className="text">{key}</span>
          </li>
        );
      })}
    </ul>
  );
};

const SessionDiffCard = ({ title, session, otherSession, color, perspective }) => (
  <div style={{
    background: 'white',
    borderRadius: '12px',
    border: `2px solid ${color}`,
    overflow: 'hidden'
  }}>
    <div style={{
      background: color,
      color: 'white',
      padding: '0.75rem 1rem',
      fontFamily: 'var(--lrbrain-font-fun)',
      fontSize: '1rem',
      fontWeight: 600
    }}>
      {title}
    </div>
    <div style={{ padding: '1rem' }}>
      {session.overview && (
        <p style={{
          fontSize: '0.875rem',
          lineHeight: '1.6',
          color: 'var(--lrbrain-text)',
          marginBottom: '0.75rem'
        }}>
          {session.overview}
        </p>
      )}

      <div style={{ marginBottom: '0.5rem' }}>
        <strong style={{ color: 'var(--lrbrain-navy)', fontSize: '0.875rem' }}>
          Topics ({(session.topics || []).length}):
        </strong>
        <DiffList items={session.topics || []} otherItems={otherSession.topics || []} perspective={perspective} />
      </div>

      <div>
        <strong style={{ color: 'var(--lrbrain-navy)', fontSize: '0.875rem' }}>
          Outcomes ({(session.learning_outcomes || []).length}):
        </strong>
        <DiffList items={session.learning_outcomes || []} otherItems={otherSession.learning_outcomes || []} perspective={perspective} />
      </div>
    </div>
  </div>
);

// (Removed unused legacy SessionSummaryCard component)

export default SessionComparison;
